import {
  StarIcon as EmptyStarIcon,
  ArrowRightIcon,
} from '@heroicons/react/outline';
import { StarIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import clsxm from '@/lib/clsxm';

import { getPresetToolHref, presets } from '@/constants/presets';
import { useUser } from '@/utils/useUser';
import { useFavoriteTools } from '@/hooks/useFavoriteTools';

interface PresetItemProps {
  preset: (typeof presets)[number];
  presetIdx: number;
  presetsLength: number;
  className?: string; // Allow custom styling (e.g., width)
}

const PresetItem = ({ preset, className }: PresetItemProps) => {
  const { t } = useLocale('tools');
  const { user } = useUser();
  const { isFavorite, toggleFavorite } = useFavoriteTools();

  const isFavourite = isFavorite(preset.slug);

  const toggleFavourites = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(preset.slug);
  };

  // For logged-in users, use the preset slug; for guests, use landingPageSlug
  const getHref = () => {
    if (user) {
      // Logged in: open the tool page directly (dashboard behavior)
      return getPresetToolHref(preset);
    } else {
      // Not logged in: use landingPageSlug for marketing pages
      return preset.isAbsoluteURL && preset.landingPageSlug
        ? preset.landingPageSlug
        : getPresetToolHref(preset);
    }
  };

  const href = getHref();

  // New Image Card Design
  if (preset.image) {
    return (
      <Link
        href={href}
        className={clsxm(
          'group relative block h-56 overflow-hidden rounded-xl shadow-sm transition-all hover:shadow-lg hover:ring-2 hover:ring-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          className,
        )}
      >
        <Image
          src={preset.image}
          alt={t(preset.title) || preset.title}
          fill
          className='object-cover transition-transform duration-300 group-hover:scale-105'
        />
        <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent' />

        <div className='absolute inset-0 flex flex-col justify-end p-5'>
          <h3 className='text-lg font-bold text-white'>
            {t(preset.title) || preset.title}
          </h3>
          <p className='mt-1 text-sm text-gray-200 line-clamp-2'>
            {t(preset.description) || preset.description}
          </p>
        </div>

        {/* Favorite Icon */}
        <button
          onClick={toggleFavourites}
          className='absolute top-3 right-3 rounded-full p-1.5 transition-colors hover:bg-black/20 text-white focus:outline-none'
        >
          {isFavourite ? (
            <StarIcon className='h-5 w-5 text-yellow-400' />
          ) : (
            <EmptyStarIcon className='h-5 w-5 text-white/70 hover:text-white' />
          )}
        </button>

        {/* Badge */}
        {preset.badge && (
          <div className='absolute top-3 left-3 rounded-full bg-primary-500/90 px-2.5 py-0.5 text-xs font-semibold text-white backdrop-blur-sm'>
            {t(preset.badge) || preset.badge}
          </div>
        )}
      </Link>
    );
  }

  // Improved Standard Card Design
  return (
    <Link
      href={href}
      className={clsxm(
        'group relative flex flex-col h-full rounded-xl bg-white p-5 shadow-sm transition-all hover:shadow-md border border-gray-100 dark:bg-slate-800 dark:border-slate-700/50 hover:border-primary-500 hover:ring-1 hover:ring-primary-500',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:ring-offset-slate-900',
        className,
      )}
    >
      <div className='flex items-start justify-between'>
        <span
          className={clsxm(
            preset.iconBackground,
            preset.iconForeground,
            'inline-flex rounded-lg p-2.5 ring-4 ring-white dark:bg-slate-800 dark:ring-1 dark:ring-white/5',
          )}
        >
          <preset.icon className='h-6 w-6' aria-hidden='true' />
        </span>

        <button
          onClick={toggleFavourites}
          className='p-2 text-gray-400 hover:text-yellow-500 dark:text-slate-500 dark:hover:text-yellow-400 transition-colors focus:outline-none'
        >
          {isFavourite ? (
            <StarIcon className='h-5 w-5 text-yellow-500' />
          ) : (
            <EmptyStarIcon className='h-5 w-5' />
          )}
        </button>
      </div>

      <div className='mt-4 flex-1'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors'>
          {t(preset.title) || preset.title}
        </h3>
        <p className='mt-1.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-3'>
          {t(preset.description) || preset.description}
        </p>
      </div>

      {preset.badge && (
        <div className='mt-4 flex'>
          <span className='inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'>
            {t(preset.badge) || preset.badge}
          </span>
        </div>
      )}
    </Link>
  );
};

export default PresetItem;
